// src/components/ecommerce/ServiceFilters.jsx
import React, { useState } from 'react';
import { 
  Clock,
  DollarSign,
  MapPin,
  Users,
  Calendar,
  Filter,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export const ServiceFilters = ({ 
  services,
  categories,
  filters,
  priceRange,
  onFiltersChange,
  onPriceRangeChange,
  styling = {},
  className = ''
}) => {
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    duration: true,
    delivery: true,
    price: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Extract unique delivery methods from services
  const deliveryMethods = [...new Set(
    services
      .map(s => s.deliveryMethod)
      .filter(Boolean)
  )];

  // Extract unique duration ranges
  const durationRanges = [
    { label: 'Under 30 min', value: '0-30' },
    { label: '30 min - 1 hour', value: '30-60' },
    { label: '1 - 2 hours', value: '60-120' },
    { label: '2+ hours', value: '120+' }
  ];

  const handleFilterChange = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFiltersChange({
      ...filters,
      [filterType]: newValues
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    onPriceRangeChange([0, 1000]);
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(arr => arr && arr.length > 0) ||
           priceRange[0] > 0 || priceRange[1] < 1000;
  };

  const FilterSection = ({ title, section, children }) => (
    <div className="border-b border-gray-200 pb-4 mb-4">
      <button
        onClick={() => toggleSection(section)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {expandedSections[section] ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>
      {expandedSections[section] && children}
    </div>
  );

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-bold text-gray-900">Filter Services</h2>
        </div>
        {hasActiveFilters() && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Clear All
          </button>
        )}
      </div>

      {/* Categories */}
      {categories && categories.length > 0 && (
        <FilterSection title="Service Type" section="category">
          <div className="space-y-2">
            {categories.map(category => {
              const count = services.filter(s => s.category === category.id || s.category === category.name).length;
              const isSelected = filters.categories?.includes(category.id) || filters.categories?.includes(category.name);
              
              return (
                <label
                  key={category.id}
                  className="flex items-center justify-between cursor-pointer group"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleFilterChange('categories', category.id || category.name)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {category.name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">({count})</span>
                </label>
              );
            })}
          </div>
        </FilterSection>
      )}

      {/* Duration */}
      <FilterSection title="Duration" section="duration">
        <div className="space-y-2">
          {durationRanges.map(range => {
            const isSelected = filters.duration?.includes(range.value);
            
            return (
              <label
                key={range.value}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleFilterChange('duration', range.value)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  {range.label}
                </span>
              </label>
            );
          })}
        </div>
      </FilterSection>

      {/* Delivery Method */}
      {deliveryMethods.length > 0 && (
        <FilterSection title="Service Location" section="delivery">
          <div className="space-y-2">
            {deliveryMethods.map(method => {
              const isSelected = filters.deliveryMethod?.includes(method);
              const label = method.replace('-', ' ').split(' ').map(w => 
                w.charAt(0).toUpperCase() + w.slice(1)
              ).join(' ');
              
              return (
                <label
                  key={method}
                  className="flex items-center gap-2 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleFilterChange('deliveryMethod', method)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
        </FilterSection>
      )}

      {/* Price Range */}
      <FilterSection title="Price Range" section="price">
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">${priceRange[0]}</span>
            <span className="text-gray-600">${priceRange[1]}+</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="0"
              max="1000"
              step="10"
              value={priceRange[1]}
              onChange={(e) => onPriceRangeChange([priceRange[0], parseInt(e.target.value)])}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, ${styling.primaryColor || '#3b82f6'} 0%, ${styling.primaryColor || '#3b82f6'} ${(priceRange[1] / 1000) * 100}%, #e5e7eb ${(priceRange[1] / 1000) * 100}%, #e5e7eb 100%)`
              }}
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="h-4 w-4 text-gray-400" />
            <span className="text-gray-700">
              Up to <span className="font-semibold">${priceRange[1]}</span> per session
            </span>
          </div>
        </div>
      </FilterSection>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Active Filters</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters).map(([type, values]) => 
              values?.map(value => (
                <span
                  key={`${type}-${value}`}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                >
                  {value}
                  <button
                    onClick={() => handleFilterChange(type, value)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};